import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { UpgradeLimitModal } from '../components/LockedFeature';
import SoftUpgradeNudge from '../components/SoftUpgradeNudge';
import { HabitsPageSkeleton } from '../components/LoadingSkeleton';
import { getColors, fonts, formatDate, timeOfDayOptions, HABIT_TEMPLATES, getModalStyle } from '../theme';
import { Plus, X, Edit2, Trash2, Clock, Save, FolderOpen, Download, Sun, Sunrise, Coffee, Moon as MoonIcon, Sparkles, ChevronLeft, ChevronRight, GripVertical, Check, Calendar, Lock, Link2, Target, AlertTriangle } from 'lucide-react';
import { getDisplayStreak, getStreakStyle } from '../utils/streakUtils';
import { InkBox, Bar, QuickAddRow } from '../components/Notebook';
import { useQuickAddInterpreter } from '../agent/quickAdd';
import HabitTrendHead, { TrendRangeToggle, useHabitTrend, useTrendRange } from '../components/HabitTrendHead';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Time of day icon mapping
const getTimeIcon = (timeOfDay) => {
  const icons = {
    morning: Sunrise,
    midday: Sun,
    afternoon: Coffee,
    evening: MoonIcon,
    anytime: Sparkles
  };
  return icons[timeOfDay] || Sparkles;
};

// Sortable habit row component
const SortableHabitRow = ({ 
  habit, 
  calendarDays, 
  completions, 
  toggleCompletion, 
  openEditModal, 
  handleDeleteHabit, 
  colors, 
  fonts, 
  todayCellRef,
  today,
  isDark,
  isLocked
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: habit.id });
  
  // Get streak info for display
  const displayStreak = getDisplayStreak(habit);
  const streakStyle = getStreakStyle(displayStreak, isDark);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (isLocked ? 0.4 : 1),
    backgroundColor: isDragging ? colors.bgTertiary : undefined,
    filter: isLocked ? 'grayscale(60%)' : undefined,
  };

  const TimeIcon = getTimeIcon(habit.timeOfDay);

  return (
    <tr 
      ref={setNodeRef} 
      className="row-actions-host"
      style={{ ...style, borderBottom: `1px solid ${colors.border}` }}
    >
      <td 
        className="nb-cell py-2.5 pr-4 sticky left-0 z-10" 
        style={{ backgroundColor: isDragging ? colors.bgTertiary : colors.bg }}
      >
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="row-fade cursor-grab active:cursor-grabbing p-1 rounded hover:bg-opacity-10 transition-colors touch-none"
            style={{ color: colors.textMuted }}
            title="Drag to reorder"
          >
            <GripVertical size={16} />
          </button>
          <button 
            onClick={() => openEditModal(habit)}
            className="flex items-center gap-3 text-left hover:opacity-70 transition-all group"
          >
            <TimeIcon size={13} strokeWidth={1.5} style={{ color: colors.textMuted }} />
            <span style={{ color: colors.textPrimary }}>
              {habit.name}
            </span>
            <Edit2 size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: colors.textMuted }} />
          </button>
          {/* Streak Badge */}
          {displayStreak > 0 && (
            <span
              className="font-mono text-xs ml-2 tabular-nums"
              style={{ color: streakStyle.isActive && displayStreak >= 3 ? colors.textPrimary : colors.textMuted }}
              title={`Current streak: ${displayStreak} day${displayStreak !== 1 ? 's' : ''}`}
            >
              {displayStreak}d
            </span>
          )}
          <button
            onClick={() => handleDeleteHabit(habit.id)}
            className="row-fade p-1.5 ml-auto transition-opacity hover:opacity-60"
            style={{ color: colors.textMuted }}
            title="Delete habit"
            aria-label={`Delete ${habit.name}`}
          >
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
        </div>
      </td>
      {calendarDays.map(day => {
        const isCompleted = completions[habit.id]?.[day.dateString];
        const isToday = day.date.toDateString() === today.toDateString();
        const isFuture = day.date > today;
        const isDisabled = isFuture || isLocked;
        
        return (
          <td key={day.dateString} className="px-1 py-2 text-center">
            <button
              onClick={(e) => {
                if (!isDisabled) {
                  // Add animation class temporarily
                  e.currentTarget.classList.add('animate-checkbox-bounce', 'animate-success-glow');
                  setTimeout(() => {
                    e.currentTarget?.classList.remove('animate-checkbox-bounce', 'animate-success-glow');
                  }, 600);
                  toggleCompletion(habit.id, day.dateString);
                }
              }}
              disabled={isDisabled}
              className={`w-[18px] h-[18px] block mx-auto transition-colors duration-150 ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              style={{
                backgroundColor: isCompleted ? colors.accent : 'transparent',
                border: `1px ${isFuture ? 'dashed' : 'solid'} ${isCompleted ? colors.accent : colors.borderStrong}`,
                outline: isToday ? `1px solid ${colors.textPrimary}` : 'none',
                outlineOffset: 2,
              }}
              aria-label={`${habit.name}, ${day.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`}
              aria-pressed={!!isCompleted}
            />
          </td>
        );
      })}
    </tr>
  );
};

// Mobile-friendly sortable habit card
const SortableMobileHabitCard = ({ 
  habit, 
  selectedDate,
  completions, 
  toggleCompletion, 
  openEditModal, 
  handleDeleteHabit, 
  colors, 
  fonts,
  isDark,
  isLocked
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: habit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (isLocked ? 0.4 : 1),
    filter: isLocked ? 'grayscale(60%)' : undefined,
  };

  const TimeIcon = getTimeIcon(habit.timeOfDay);
  const dateStr = formatDate(selectedDate);
  const isCompleted = completions[habit.id]?.[dateStr];
  const isFuture = selectedDate > new Date();
  
  // Get streak info for display
  const displayStreak = getDisplayStreak(habit);
  const streakStyle = getStreakStyle(displayStreak, isDark);

  return (
    <div 
      ref={setNodeRef} 
      className="flex items-center gap-3 py-3 min-h-[56px] transition-colors"
      style={{ 
        ...style,
        borderBottom: `1px solid ${colors.border}`,
        position: 'relative',
      }}
    >
      {/* Locked overlay with upgrade hint */}
      {isLocked && (
        <div 
          className="absolute inset-0 rounded-lg flex items-center justify-end pr-14 cursor-pointer z-10"
          style={{ backgroundColor: 'transparent' }}
          onClick={() => window.location.href = '/upgrade'}
          title="Upgrade to Pro to unlock this habit"
        >
          <Lock size={16} style={{ color: colors.textMuted }} />
        </div>
      )}
      
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 rounded touch-none"
        style={{ color: colors.textMuted }}
      >
        <GripVertical size={18} />
      </button>

      {/* Completion Toggle */}
      <button
        onClick={(e) => {
          if (!isFuture && !isLocked) {
            // Add animation class temporarily
            e.currentTarget.classList.add('animate-checkbox-bounce', 'animate-success-glow');
            setTimeout(() => {
              e.currentTarget?.classList.remove('animate-checkbox-bounce', 'animate-success-glow');
            }, 600);
            toggleCompletion(habit.id, dateStr);
          }
        }}
        disabled={isFuture || isLocked}
        className={`w-11 h-11 -m-2.5 flex items-center justify-center ${isFuture || isLocked ? 'opacity-30 cursor-not-allowed' : ''}`}
        aria-label={`${habit.name}, done`}
        aria-pressed={!!isCompleted}
      >
        <InkBox checked={!!isCompleted} size={20} />
      </button>

      {/* Habit Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <TimeIcon size={14} className="flex-shrink-0" style={{ color: colors.textMuted }} />
          <span 
            className="text-[15px] leading-tight"
            style={{ color: colors.textPrimary }}
          >
            {habit.name}
          </span>
          {/* Streak Badge */}
        </div>
        <span className="font-mono text-xs capitalize ml-5" style={{ color: colors.textMuted }}>
          {habit.timeOfDay || 'Anytime'}
        </span>
      </div>

      {displayStreak > 0 && (
        <span
          className="font-mono text-xs tabular-nums"
          style={{ color: streakStyle.isActive && displayStreak >= 3 ? colors.textPrimary : colors.textMuted }}
          title={`Current streak: ${displayStreak} day${displayStreak !== 1 ? 's' : ''}`}
        >
          {displayStreak}d
        </span>
      )}
      <button
        onClick={() => openEditModal(habit)}
        className="p-2.5 -mr-2"
        style={{ color: colors.textMuted }}
        aria-label={`Edit ${habit.name}`}
      >
        <Edit2 size={16} strokeWidth={1.5} />
      </button>
    </div>
  );
};

const HabitsPage = () => {
  const { isDark, habits, completions, addHabit, addHabits, updateHabit, deleteHabit, reorderHabits, toggleCompletion, templates, saveTemplate, loadTemplate, deleteTemplate, dataLoading, goals, unlinkHabitFromGoals } = useApp();
  const { canAddMore, getItemLimit, isPro, hasFeature } = useSubscription();
  const colors = getColors(isDark);
  const interpretHabit = useQuickAddInterpreter('habit');
  
  // Check if user can add more habits
  const habitLimit = getItemLimit('maxHabits');
  const canAddHabit = canAddMore('maxHabits', habits.length);
  const isAtLimit = !canAddHabit && habitLimit !== Infinity;
  
  // Calendar state - starts with current month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  
  // Mobile view - selected date for daily view
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Refs
  const scrollContainerRef = useRef(null);
  const todayCellRef = useRef(null);
  const mobileHeatmapRef = useRef(null);
  
  // Track if dragging is in progress
  const isDraggingRef = useRef(false);
  const savedScrollRef = useRef(0);
  
  // Maintain scroll position during drag (prevents trackpad momentum scrolling)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      // If dragging, force scroll back to saved position
      if (isDraggingRef.current && savedScrollRef.current !== container.scrollLeft) {
        container.scrollLeft = savedScrollRef.current;
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [calendarDays, habits.length]);
  
  // Modal states
  const [showHabitModal, setShowHabitModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingHabit, setEditingHabit] = useState(null);
  const [habitForm, setHabitForm] = useState({ name: '', timeOfDay: 'anytime' });
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [unlinkWarningHabit, setUnlinkWarningHabit] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [trendDays, setTrendDays] = useTrendRange();
  const { series: trendSeries, average: trendAverage } = useHabitTrend(habits, completions, trendDays);
  const [heatMapYear, setHeatMapYear] = useState(new Date().getFullYear());
  const [heatMapMonth, setHeatMapMonth] = useState(new Date().getMonth());
  const [heatMapView, setHeatMapView] = useState('year'); // 'year' or 'month'

  // Check if user has analytics access (Pro feature)
  const hasAnalyticsAccess = hasFeature('analytics');
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track scroll position to preserve it during drag
  const scrollPositionRef = useRef(0);

  // Handle drag start - save scroll position
  const handleDragStart = () => {
    isDraggingRef.current = true;
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollPositionRef.current = currentScroll;
      savedScrollRef.current = currentScroll;
    }
  };

  // Handle drag end - reorder habits and restore scroll position
  const handleDragEnd = (event) => {
    const { active, over } = event;
    const savedScroll = scrollPositionRef.current;
    
    if (active.id !== over?.id) {
      const oldIndex = habits.findIndex(h => h.id === active.id);
      const newIndex = habits.findIndex(h => h.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newHabits = arrayMove(habits, oldIndex, newIndex);
        reorderHabits(newHabits);
      }
    }
    
    // Restore scroll position after React re-renders
    const restoreScroll = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = savedScroll;
      }
    };
    
    restoreScroll();
    requestAnimationFrame(restoreScroll);
    
    // Clear drag flag after a short delay to allow final restores
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 100);
  };
  
  // Desktop grid: as many past days as fit the width, ending today (future days can't be checked,
  // so they only took space). Re-fits when the window resizes.
  const [dayCount, setDayCount] = useState(21);
  const hasHabits = habits.length > 0;
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const NAME_COL = 260; // habit name, streak and drag handle
    const DAY_COL = 30;
    const fit = () => {
      const width = container.clientWidth;
      if (!width) return; // hidden on phones
      setDayCount(Math.max(7, Math.min(90, Math.floor((width - NAME_COL) / DAY_COL))));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [hasHabits]);

  useEffect(() => {
    const days = [];
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    for (let i = -(dayCount - 1); i <= 0; i++) {
      const date = new Date(todayDate);
      date.setDate(todayDate.getDate() + i);
      days.push({
        date,
        dateString: formatDate(date),
        dayOfWeek: date.getDay(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    
    setCalendarDays(days);
  }, [dayCount]);
  
  // Today is the last column; if a long habit name still forces overflow, keep it in view
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && !isDraggingRef.current) container.scrollLeft = container.scrollWidth;
  }, [calendarDays, habits.length]);
  

  // Heat map data calculation
  const heatMapData = useMemo(() => {
    if (!hasAnalyticsAccess) return { weeks: [], stats: {}, monthWeeks: [] };

    const today = new Date();
    const todayStr = formatDate(today);
    const yearStart = new Date(heatMapYear, 0, 1);
    const yearEnd = new Date(heatMapYear, 11, 31);
    
    // For month view
    const monthStart = new Date(heatMapYear, heatMapMonth, 1);
    const monthEnd = new Date(heatMapYear, heatMapMonth + 1, 0);
    
    const data = [];

    // Helper to get week number
    const getWeekNumber = (date) => {
      const yearStart = new Date(date.getFullYear(), 0, 1);
      const dayOfYear = Math.floor((date - yearStart) / (24 * 60 * 60 * 1000));
      return Math.floor((dayOfYear + yearStart.getDay()) / 7);
    };

    // Calculate completion rate for each day (entire year, including future)
    const currentDate = new Date(yearStart);
    let totalCompletions = 0;
    let totalPossible = 0;
    let perfectDays = 0;
    let currentStreak = 0;
    let maxStreak = 0;

    while (currentDate <= yearEnd) {
      const dateStr = formatDate(currentDate);
      const isFuture = currentDate > today;
      let completedCount = 0;

      if (!isFuture) {
        habits.forEach(habit => {
          if (completions[habit.id]?.[dateStr]) {
            completedCount++;
          }
        });

        const totalHabits = habits.length || 1;
        const completionRate = Math.round((completedCount / totalHabits) * 100);

        // Track stats (only for past/present days)
        totalCompletions += completedCount;
        totalPossible += habits.length;

        if (completionRate === 100 && habits.length > 0) {
          perfectDays++;
          currentStreak++;
          maxStreak = Math.max(maxStreak, currentStreak);
        } else {
          currentStreak = 0;
        }
      }

      data.push({
        date: dateStr,
        completions: isFuture ? null : completedCount,
        total: habits.length,
        rate: isFuture ? null : Math.round((completedCount / (habits.length || 1)) * 100),
        dayOfWeek: currentDate.getDay(),
        week: getWeekNumber(currentDate),
        month: currentDate.getMonth(),
        isFuture,
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Group by weeks (full year)
    const weeks = [];
    let currentWeek = [];
    let lastWeekNum = -1;

    data.forEach(day => {
      if (day.week !== lastWeekNum && currentWeek.length > 0) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentWeek.push(day);
      lastWeekNum = day.week;
    });

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    // Group by weeks (current month only) and calculate month stats
    const monthData = data.filter(d => d.month === heatMapMonth);
    const monthWeeks = [];
    let monthCurrentWeek = [];
    let monthLastWeekNum = -1;

    // Month-specific stats
    let monthTotalCompletions = 0;
    let monthTotalPossible = 0;
    let monthPerfectDays = 0;
    let monthCurrentStreak = 0;
    let monthMaxStreak = 0;

    monthData.forEach(day => {
      if (day.week !== monthLastWeekNum && monthCurrentWeek.length > 0) {
        monthWeeks.push(monthCurrentWeek);
        monthCurrentWeek = [];
      }
      monthCurrentWeek.push(day);
      monthLastWeekNum = day.week;

      // Calculate month stats (only for non-future days)
      if (!day.isFuture && day.completions !== null) {
        monthTotalCompletions += day.completions;
        monthTotalPossible += day.total;
        if (day.rate === 100 && day.total > 0) {
          monthPerfectDays++;
          monthCurrentStreak++;
          monthMaxStreak = Math.max(monthMaxStreak, monthCurrentStreak);
        } else {
          monthCurrentStreak = 0;
        }
      }
    });

    if (monthCurrentWeek.length > 0) {
      monthWeeks.push(monthCurrentWeek);
    }

    // Calculate how many weeks belong to each month (for label alignment)
    const monthWeekCounts = Array(12).fill(0);
    weeks.forEach(week => {
      // Use the month of the first day in the week that has data
      const firstDayWithData = week.find(d => d.month !== undefined);
      if (firstDayWithData) {
        // Count weeks by the month of their Thursday (ISO standard) or first day
        const midWeekDay = week.find(d => d.dayOfWeek === 4) || firstDayWithData;
        monthWeekCounts[midWeekDay.month]++;
      }
    });

    const avgCompletionRate = totalPossible > 0 ? Math.round((totalCompletions / totalPossible) * 100) : 0;
    const monthAvgCompletionRate = monthTotalPossible > 0 ? Math.round((monthTotalCompletions / monthTotalPossible) * 100) : 0;

    return {
      weeks,
      monthWeeks,
      monthWeekCounts,
      stats: {
        totalCompletions,
        perfectDays,
        maxStreak,
        avgCompletionRate,
      },
      monthStats: {
        totalCompletions: monthTotalCompletions,
        perfectDays: monthPerfectDays,
        maxStreak: monthMaxStreak,
        avgCompletionRate: monthAvgCompletionRate,
      }
    };
  }, [habits, completions, heatMapYear, heatMapMonth, hasAnalyticsAccess]);

  // Auto-scroll mobile heatmap to center on current week
  useEffect(() => {
    if (mobileHeatmapRef.current && heatMapView === 'year' && heatMapData.weeks.length > 0) {
      const container = mobileHeatmapRef.current;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      // Find the week containing today
      let currentWeekIndex = heatMapData.weeks.findIndex(week => 
        week.some(day => day.date === todayStr)
      );
      
      // If today not found (viewing different year), scroll to end for past years, start for future
      if (currentWeekIndex === -1) {
        currentWeekIndex = heatMapYear < today.getFullYear() ? heatMapData.weeks.length - 1 : 0;
      }
      
      // Calculate scroll position to center the current week
      const cellWidth = 10 + 2; // 10px cell + 2px gap (gap-0.5 = 2px)
      const containerWidth = container.offsetWidth;
      const scrollTo = (currentWeekIndex * cellWidth) - (containerWidth / 2) + (cellWidth / 2);
      
      container.scrollTo({ left: Math.max(0, scrollTo), behavior: 'instant' });
    }
  }, [heatMapView, heatMapYear, heatMapData.weeks]);

  // Get color for heat map cell based on completion rate
  const getHeatMapColor = (rate, isFuture) => {
    // Future days get a very subtle empty square
    if (isFuture || rate === null) return isDark ? `${colors.border}20` : `${colors.border}40`;
    if (rate === 0) return isDark ? colors.bgTertiary : colors.border;
    if (rate < 25) return `${colors.accent}30`;
    if (rate < 50) return `${colors.accent}50`;
    if (rate < 75) return `${colors.accent}80`;
    return colors.accent;
  };


  // Handle habit form
  const openAddModal = (prefill) => {
    setEditingHabit(null);
    setHabitForm({ name: typeof prefill === 'string' ? prefill : '', timeOfDay: 'anytime' });
    setSelectedGoalIds([]);
    setShowHabitModal(true);
  };
  
  // The next line of the list: type a name and press Enter. Laid out bare so the input lines up
  // with the habit names above it (desktop: after the drag slot and time icon; phone: after the
  // drag slot, the check box and the time icon).
  const quickAddProps = {
    placeholder: 'Add a habit…',
    hint: '↵ to add',
    onAdd: (name) => addHabit(name),
    interpret: interpretHabit,
    onDetails: openAddModal,
    detailsWhenEmpty: false,
    blocked: isAtLimit,
    onBlocked: () => setShowUpgradeModal(true),
    autoFocus: true,
  };
  const desktopHabitQuickAdd = (
    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
      {/* Spans the table; the zero-width wrapper keeps typing from resizing the name column */}
      <td colSpan={calendarDays.length + 1} className="p-0">
        <div className="w-0 min-w-full flex items-center gap-2">
          <span className="w-6 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0 flex items-center gap-3">
            <QuickAddRow bare {...quickAddProps} mark={<Plus size={13} strokeWidth={1.5} />} />
          </div>
        </div>
      </td>
    </tr>
  );
  const mobileHabitQuickAdd = (
    <div className="flex items-center gap-3 min-h-[56px]" style={{ borderBottom: `1px solid ${colors.border}` }}>
      <span className="w-[26px] flex-shrink-0" aria-hidden="true" />
      <span className="w-6 flex-shrink-0 flex items-center justify-center" aria-hidden="true">
        <span className="block w-5 h-5" style={{ border: `1.5px dashed ${colors.borderStrong}` }} />
      </span>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <QuickAddRow bare {...quickAddProps} mark={<span className="w-[14px]" />} />
      </div>
    </div>
  );
  const newHabitButton = (label) => (
    <button
      onClick={() => (isAtLimit ? setShowUpgradeModal(true) : openAddModal())}
      className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors duration-150 ${isAtLimit ? 'opacity-75' : ''}`}
      style={{ backgroundColor: colors.accent, color: colors.onAccent }}
      aria-label="New habit"
    >
      <Plus size={14} />
      {label}
      {isAtLimit && <span className="text-[10px]">({habits.length}/{habitLimit})</span>}
    </button>
  );
  
  // The heading band: trend line with the title resting on it (components/HabitTrendHead.js)
  const trendHead = (compact, actions) => (
    <HabitTrendHead
      compact={compact}
      series={trendSeries}
      average={trendAverage}
      rangeDays={trendDays}
      meta={<span className="tabular-nums">average {trendAverage}% · {habits.length} habit{habits.length === 1 ? '' : 's'}</span>}
      controls={<TrendRangeToggle value={trendDays} onChange={setTrendDays} />}
      actions={actions}
    />
  );
  
  const openEditModal = (habit) => {
    setEditingHabit(habit);
    setHabitForm({ name: habit.name, timeOfDay: habit.timeOfDay || 'anytime' });
    setSelectedGoalIds(habit.linkedGoalIds || []);
    setShowHabitModal(true);
  };
  
  const handleSaveHabit = () => {
    if (!habitForm.name.trim()) return;
    
    if (editingHabit) {
      updateHabit(editingHabit.id, { ...habitForm, linkedGoalIds: selectedGoalIds });
    } else {
      addHabit({ ...habitForm, name: habitForm.name.trim(), linkedGoalIds: selectedGoalIds });
    }
    setShowHabitModal(false);
  };
  
  // Handle delete with unlink warning for active linked goals
  const handleDeleteHabit = (id) => {
    const habit = habits.find(h => h.id === id);
    const linkedGoalIds = habit?.linkedGoalIds || [];
    
    // Check if any linked goals are active (not completed)
    const activeLinkedGoals = goals.filter(g => 
      linkedGoalIds.includes(g.id) && !g.completed
    );
    
    if (activeLinkedGoals.length > 0) {
      // Show warning modal
      setUnlinkWarningHabit({ habit, activeLinkedGoals });
    } else {
      // No active linked goals, delete directly
      deleteHabit(id);
    }
  };
  
  const confirmDeleteWithUnlink = () => {
    if (unlinkWarningHabit?.habit) {
      deleteHabit(unlinkWarningHabit.habit.id);
    }
    setUnlinkWarningHabit(null);
  };
  
  // Get active goals for linking (exclude completed)
  const activeGoals = goals.filter(g => !g.completed);
  
  // Template functions
  const handleSaveTemplate = () => {
    if (!templateName.trim()) return;
    saveTemplate(templateName.trim());
    setTemplateName('');
    setShowTemplateModal(false);
  };
  
  const handleLoadTemplate = (template) => {
    loadTemplate(template.id);
    setShowTemplateModal(false);
  };

  const handleLoadStarterTemplate = (template) => {
    addHabits(template.habits);
    setShowTemplateModal(false);
  };
  
  const today = new Date();
  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  const isSelectedToday = selectedDate.toDateString() === today.toDateString();
  
  // Get continuous range of days: 30 days back + today + 7 days forward
  const getMobileDays = () => {
    const days = [];
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    // 30 days before today
    for (let i = 30; i >= 1; i--) {
      const day = new Date(todayDate);
      day.setDate(todayDate.getDate() - i);
      days.push(day);
    }
    
    // Today
    days.push(new Date(todayDate));
    
    // 7 days after today
    for (let i = 1; i <= 7; i++) {
      const day = new Date(todayDate);
      day.setDate(todayDate.getDate() + i);
      days.push(day);
    }
    
    return days;
  };
  
  const mobileDays = getMobileDays();
  const todayIndex = 30; // Today is at index 30 (after 30 days back)
  
  // Track the visible month based on scroll position
  const [visibleMonth, setVisibleMonth] = useState(() => {
    return today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  });
  
  // Ref for horizontal scroll container
  const mobileScrollRef = useRef(null);
  
  // Scroll to center today on mount
  useEffect(() => {
    if (mobileScrollRef.current) {
      const container = mobileScrollRef.current;
      const itemWidth = 52; // Approximate width of each day button including gap
      const containerWidth = container.offsetWidth;
      const scrollTo = (todayIndex * itemWidth) - (containerWidth / 2) + (itemWidth / 2);
      // Use instant scroll on mount, smooth scroll otherwise
      container.scrollTo({ left: Math.max(0, scrollTo), behavior: 'instant' });
    }
  }, [todayIndex]);
  
  // Handle scroll to detect visible month
  const handleScroll = () => {
    if (mobileScrollRef.current) {
      const container = mobileScrollRef.current;
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.offsetWidth;
      const itemWidth = 52;
      
      // Calculate the center visible index
      const centerIndex = Math.round((scrollLeft + containerWidth / 2) / itemWidth);
      const clampedIndex = Math.max(0, Math.min(centerIndex, mobileDays.length - 1));
      const centerDay = mobileDays[clampedIndex];
      
      if (centerDay) {
        const monthYear = centerDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        setVisibleMonth(monthYear);
      }
    }
  };
  
  // Calculate completion stats for mobile
  const completedToday = habits.filter(h => completions[h.id]?.[formatDate(selectedDate)]).length;
  const completionPercent = habits.length > 0 ? Math.round((completedToday / habits.length) * 100) : 0;

  // Show skeleton while data is loading
  if (dataLoading) {
    return (
      <div className="min-h-screen pb-16 px-4 pt-4" style={{ backgroundColor: 'transparent' }}>
        <HabitsPageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: 'transparent' }}>
      
      {/* ==================== MOBILE VIEW ==================== */}
      <div className="md:hidden pt-5">
        {/* Mobile Header: trend band with the title resting on the line */}
        <div className="mb-5 animate-fade-in animate-delay-1">
          <div className="mb-3">
            {trendHead(true, (
              <>
                <button
                  onClick={() => setShowTemplateModal(true)}
                  className="p-2.5"
                  style={{ border: `1px solid ${colors.borderStrong}`, color: colors.textSecondary }}
                  aria-label="Templates"
                >
                  <FolderOpen size={18} strokeWidth={1.5} />
                </button>
                {newHabitButton(null)}
              </>
            ))}
          </div>
          
          {/* Month/Year Header - auto-updates based on scroll position */}
          <div className="flex items-center justify-between mb-4">
            <span style={{ color: colors.textSecondary }} className="font-mono text-xs">
              {visibleMonth}
            </span>
            {!isSelectedToday && (
              <button
                onClick={() => {
                  setSelectedDate(new Date());
                  // Scroll to today
                  if (mobileScrollRef.current) {
                    const container = mobileScrollRef.current;
                    const itemWidth = 52;
                    const containerWidth = container.offsetWidth;
                    const scrollTo = (todayIndex * itemWidth) - (containerWidth / 2) + (itemWidth / 2);
                    container.scrollTo({ left: Math.max(0, scrollTo), behavior: 'smooth' });
                  }
                }}
                className="font-mono text-xs px-2 py-1"
                style={{ color: colors.textPrimary, border: `1px solid ${colors.borderStrong}` }}
              >
                Today
              </button>
            )}
          </div>
          
          {/* Continuous Day Scroller - 30 days back + today + 7 days forward */}
          <div className="relative mb-4">
            <div 
              ref={mobileScrollRef}
              onScroll={handleScroll}
              className="flex gap-1 py-2 overflow-x-auto scrollbar-hide"
              style={{ 
                borderTop: `1px solid ${colors.textPrimary}`,
                borderBottom: `1px solid ${colors.border}`,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {mobileDays.map((day, i) => {
                const isSelected = day.toDateString() === selectedDate.toDateString();
                const isTodayDate = day.toDateString() === today.toDateString();
                const dayStr = formatDate(day);
                const completed = habits.filter(h => completions[h.id]?.[dayStr]).length;
                const hasData = completed > 0;
                const isFuture = day > today;
                
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedDate(day)}
                    className="flex-shrink-0 w-11 py-2 font-mono transition-colors"
                    style={{
                      backgroundColor: isTodayDate ? colors.accent : 'transparent',
                      border: isSelected && !isTodayDate ? `1px solid ${colors.textPrimary}` : '1px solid transparent',
                      opacity: isFuture ? 0.4 : 1,
                    }}
                  >
                    <div 
                      className="text-xs font-medium"
                      style={{ color: isTodayDate ? colors.onAccent : colors.textMuted }}
                    >
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'][day.getDay()]}
                    </div>
                    <div 
                      className="text-sm font-medium"
                      style={{ color: isTodayDate ? colors.onAccent : isSelected ? colors.accent : colors.textPrimary }}
                    >
                      {day.getDate()}
                    </div>
                    {hasData && !isTodayDate && (
                      <div 
                        className="w-1.5 h-1.5 mx-auto mt-1"
                        style={{ backgroundColor: colors.textPrimary }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Day summary */}
          <div className="flex items-end gap-4 pt-4 pb-5">
            <span className="font-mono font-light text-[56px] leading-[.8] tabular-nums" style={{ color: colors.textPrimary, letterSpacing: '-0.05em' }}>
              {completionPercent}
            </span>
            <div className="flex-1 pb-1">
              <p className="text-[13px] mb-2" style={{ color: colors.textPrimary }}>
                {completedToday} of {habits.length} done · <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>{selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </p>
              <Bar value={completionPercent} max={9999} />
            </div>
          </div>
        </div>
        
        {/* Mobile Habits List */}
        {habits.length === 0 ? (
          <div style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
            {mobileHabitQuickAdd}
            <p style={{ color: colors.textMuted }} className="py-8 text-sm">No habits yet. Type a name above and press Enter, or tap + for more options.</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={habits.map(h => h.id)}
              strategy={verticalListSortingStrategy}
            >
              {habits.map((habit, index) => (
                <SortableMobileHabitCard
                  key={habit.id}
                  habit={habit}
                  selectedDate={selectedDate}
                  completions={completions}
                  toggleCompletion={toggleCompletion}
                  openEditModal={openEditModal}
                  handleDeleteHabit={handleDeleteHabit}
                  colors={colors}
                  fonts={fonts}
                  isDark={isDark}
                  isLocked={habitLimit !== Infinity && index >= habitLimit}
                />
              ))}
            </SortableContext>
            {mobileHabitQuickAdd}
          </DndContext>
        )}
        
        {/* Mobile Heat Map - Pro Feature */}
        {habits.length > 0 && (
          <div className="mt-8 mb-4">
            {/* Mobile-optimized header: heading left, controls right */}
            <div className="flex items-center justify-between mb-3">
              <h3
                className="text-base font-medium flex items-center gap-2"
                style={{ color: colors.textPrimary }}
              >
                <Calendar size={16} style={{ color: colors.accent }} />
                {heatMapView === 'year' ? 'Yearly' : 'Monthly'} Consistency
                {!hasAnalyticsAccess && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>PRO</span>
                )}
              </h3>
              {hasAnalyticsAccess && (
                <div className="flex items-center gap-2">
                  {/* View Toggle */}
                  <div 
                    className="flex rounded-lg overflow-hidden"
                    style={{ backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}` }}
                  >
                    <button
                      onClick={() => setHeatMapView('year')}
                      className="px-3 py-1 text-xs font-medium transition-all"
                      style={{ 
                        backgroundColor: heatMapView === 'year' ? colors.accent : 'transparent',
                        color: heatMapView === 'year' ? colors.onAccent : colors.textMuted
                      }}
                    >
                      Y
                    </button>
                    <button
                      onClick={() => setHeatMapView('month')}
                      className="px-3 py-1 text-xs font-medium transition-all"
                      style={{ 
                        backgroundColor: heatMapView === 'month' ? colors.accent : 'transparent',
                        color: heatMapView === 'month' ? colors.onAccent : colors.textMuted
                      }}
                    >
                      M
                    </button>
                  </div>
                  {/* Navigation */}
                  <div className="flex items-center">
                    <button
                      onClick={() => {
                        if (heatMapView === 'month') {
                          if (heatMapMonth === 0) {
                            setHeatMapYear(y => y - 1);
                            setHeatMapMonth(11);
                          } else {
                            setHeatMapMonth(m => m - 1);
                          }
                        } else {
                          setHeatMapYear(y => y - 1);
                        }
                      }}
                      className="p-1 rounded transition-all hover:opacity-70"
                      style={{ backgroundColor: colors.bgSecondary }}
                    >
                      <ChevronLeft size={14} style={{ color: colors.textMuted }} />
                    </button>
                    <span className="text-xs font-medium px-1 min-w-[50px] text-center" style={{ color: colors.textPrimary }}>
                      {heatMapView === 'month' 
                        ? new Date(heatMapYear, heatMapMonth).toLocaleDateString('en-US', { month: 'short' })
                        : heatMapYear
                      }
                    </span>
                    <button
                      onClick={() => {
                        const now = new Date();
                        if (heatMapView === 'month') {
                          if (heatMapYear === now.getFullYear() && heatMapMonth >= now.getMonth()) return;
                          if (heatMapMonth === 11) {
                            setHeatMapYear(y => y + 1);
                            setHeatMapMonth(0);
                          } else {
                            setHeatMapMonth(m => m + 1);
                          }
                        } else {
                          setHeatMapYear(y => Math.min(y + 1, now.getFullYear()));
                        }
                      }}
                      disabled={heatMapView === 'year' ? heatMapYear >= new Date().getFullYear() : (heatMapYear === new Date().getFullYear() && heatMapMonth >= new Date().getMonth())}
                      className="p-1 rounded transition-all hover:opacity-70 disabled:opacity-30"
                      style={{ backgroundColor: colors.bgSecondary }}
                    >
                      <ChevronRight size={14} style={{ color: colors.textMuted }} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {hasAnalyticsAccess ? (
              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.border}`
                }}
              >
                {/* Stats Row */}
                <div className="flex justify-between mb-4 text-center">
                  <div>
                    <p className="text-lg font-bold" style={{ color: colors.accent }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.avgCompletionRate : heatMapData.stats.avgCompletionRate}%
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Avg Rate</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold" style={{ color: colors.warning }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.perfectDays : heatMapData.stats.perfectDays}
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Perfect Days</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold" style={{ color: colors.error }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.maxStreak : heatMapData.stats.maxStreak}
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Best Streak</p>
                  </div>
                </div>

                {/* Heat Map Grid - Different display for month vs year */}
                {heatMapView === 'month' ? (
                  /* Monthly view: Calendar-style grid with larger cells */
                  <div>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={i} className="text-[10px] text-center font-medium" style={{ color: colors.textMuted }}>
                          {day}
                        </div>
                      ))}
                    </div>
                    {/* Calendar grid - rows are weeks */}
                    <div className="grid grid-cols-7 gap-1">
                      {heatMapData.monthWeeks.flatMap((week, weekIndex) =>
                        [0, 1, 2, 3, 4, 5, 6].map(dayOfWeek => {
                          const day = week.find(d => d.dayOfWeek === dayOfWeek);
                          const isCurrentMonth = day && new Date(day.date).getMonth() === heatMapMonth;
                          return (
                            <div
                              key={`${weekIndex}-${dayOfWeek}`}
                              className="aspect-square rounded-md flex items-center justify-center text-[10px] font-medium"
                              style={{
                                backgroundColor: day && isCurrentMonth ? getHeatMapColor(day.rate, day.isFuture) : 'transparent',
                                color: day && isCurrentMonth && !day.isFuture ? (day.rate >= 50 ? colors.onAccent : colors.textMuted) : colors.textMuted,
                                opacity: isCurrentMonth ? 1 : 0.3
                              }}
                              title={day && !day.isFuture ? `${day.date}: ${day.completions}/${day.total} (${day.rate}%)` : day?.date || ''}
                            >
                              {day ? new Date(day.date).getDate() : ''}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  /* Yearly view: Horizontal scroll with small cells */
                  <div ref={mobileHeatmapRef} className="overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    <div className="flex gap-[2px]" style={{ minWidth: 'max-content' }}>
                      {heatMapData.weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="flex flex-col gap-[2px]">
                          {[0, 1, 2, 3, 4, 5, 6].map(dayOfWeek => {
                            const day = week.find(d => d.dayOfWeek === dayOfWeek);
                            return (
                              <div
                                key={dayOfWeek}
                                className="w-[10px] h-[10px] rounded-sm"
                                style={{
                                  backgroundColor: day ? getHeatMapColor(day.rate, day.isFuture) : (isDark ? `${colors.border}20` : `${colors.border}40`),
                                }}
                                title={day && !day.isFuture ? `${day.date}: ${day.completions}/${day.total} (${day.rate}%)` : day?.date || ''}
                              />
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div className="flex items-center justify-end gap-1 mt-3">
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>Less</span>
                  {[0, 25, 50, 75, 100].map(rate => (
                    <div
                      key={rate}
                      className="w-[10px] h-[10px] rounded-sm"
                      style={{ backgroundColor: getHeatMapColor(rate, false) }}
                    />
                  ))}
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>More</span>
                </div>
              </div>
            ) : (
              <div
                className="pt-5 pb-2 text-center"
                style={{ borderTop: `1px solid ${colors.textPrimary}` }}
              >
                <Lock size={24} className="mx-auto mb-2" style={{ color: colors.textMuted }} />
                <p className="text-sm mb-3" style={{ color: colors.textMuted }}>
                  Track your yearly consistency patterns
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                >
                  Upgrade to Pro
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== DESKTOP VIEW ==================== */}
      <div className="hidden md:block">
        
        <div className="mb-6">
          {trendHead(false, (
            <>
              <button
                onClick={() => setShowTemplateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-xs rounded-md transition-all duration-200 font-medium"
                style={{ color: colors.textSecondary, border: `1px solid ${colors.borderStrong}` }}
              >
                <FolderOpen size={14} strokeWidth={1.5} />
                Templates
              </button>
              {newHabitButton('New habit')}
              {isAtLimit && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-xs font-medium hover:underline transition-all"
                  style={{ color: colors.accent }}
                >
                  Upgrade →
                </button>
              )}
            </>
          ))}
        </div>
        
        {/* Soft upgrade nudge - shows when approaching limit */}
        <SoftUpgradeNudge 
          current={habits.length}
          limit={habitLimit}
          itemType="habits"
          colors={colors}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
        
        {/* Habits Grid */}
        {habits.length === 0 ? (
          <div style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
            <table className="w-full"><tbody>{desktopHabitQuickAdd}</tbody></table>
            <p style={{ color: colors.textMuted }} className="py-10">No habits yet. Type a name above and press Enter, or start from a template.</p>
          </div>
        ) : (
          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto"
            style={{ borderTop: `1px solid ${colors.textPrimary}` }}
          >
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th className="text-left py-3 pr-4 sticky left-0 z-10" style={{ backgroundColor: colors.bg, minWidth: '200px' }}>
                    <span className="font-mono text-xs font-normal" style={{ color: colors.textSecondary }}>
                      {today.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  </th>
                  {calendarDays.map(day => {
                    const isToday = day.date.toDateString() === today.toDateString();
                    const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.date.getDay()];
                    return (
                      <th 
                        key={day.dateString}
                        ref={isToday ? todayCellRef : null}
                        className="px-1 py-2 text-center font-mono font-normal"
                        style={{ minWidth: '28px' }}
                      >
                        <div className="text-[10px] leading-tight" style={{ color: colors.textMuted }}>
                          {dayOfWeek[0]}
                        </div>
                        <div
                          className="text-[11px] leading-tight mx-auto w-[18px] tabular-nums"
                          style={{
                            color: isToday ? colors.onAccent : colors.textPrimary,
                            backgroundColor: isToday ? colors.accent : 'transparent',
                          }}
                        >
                          {day.date.getDate()}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={habits.map(h => h.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {habits.map((habit, index) => (
                      <SortableHabitRow
                        key={habit.id}
                        habit={habit}
                        calendarDays={calendarDays}
                        completions={completions}
                        toggleCompletion={toggleCompletion}
                        openEditModal={openEditModal}
                        handleDeleteHabit={handleDeleteHabit}
                        colors={colors}
                        fonts={fonts}
                        todayCellRef={todayCellRef}
                        today={today}
                        isDark={isDark}
                        isLocked={habitLimit !== Infinity && index >= habitLimit}
                      />
                    ))}
                    {desktopHabitQuickAdd}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        )}
        
        {/* Desktop Heat Map - Pro Feature */}
        {habits.length > 0 && (
          <div className="mt-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3
                className="text-lg flex items-center gap-2 font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                <Calendar size={20} style={{ color: colors.accent }} />
                {heatMapView === 'year' ? 'Yearly' : 'Monthly'} Consistency
                {!hasAnalyticsAccess && (
                  <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>PRO</span>
                )}
              </h3>
              {hasAnalyticsAccess && (
                <div className="flex items-center gap-3">
                  {/* View Toggle */}
                  <div 
                    className="flex rounded-lg overflow-hidden"
                    style={{ backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}` }}
                  >
                    <button
                      onClick={() => setHeatMapView('year')}
                      className="px-3 py-1 text-xs font-medium transition-all"
                      style={{ 
                        backgroundColor: heatMapView === 'year' ? colors.accent : 'transparent',
                        color: heatMapView === 'year' ? colors.onAccent : colors.textMuted
                      }}
                    >
                      Year
                    </button>
                    <button
                      onClick={() => setHeatMapView('month')}
                      className="px-3 py-1 text-xs font-medium transition-all"
                      style={{ 
                        backgroundColor: heatMapView === 'month' ? colors.accent : 'transparent',
                        color: heatMapView === 'month' ? colors.onAccent : colors.textMuted
                      }}
                    >
                      Month
                    </button>
                  </div>
                  {/* Navigation */}
                  <button
                    onClick={() => {
                      if (heatMapView === 'month') {
                        if (heatMapMonth === 0) {
                          setHeatMapYear(y => y - 1);
                          setHeatMapMonth(11);
                        } else {
                          setHeatMapMonth(m => m - 1);
                        }
                      } else {
                        setHeatMapYear(y => y - 1);
                      }
                    }}
                    className="p-1.5 rounded-lg transition-all hover:opacity-70"
                    style={{ backgroundColor: colors.bgSecondary }}
                  >
                    <ChevronLeft size={16} style={{ color: colors.textSecondary }} />
                  </button>
                  <span className="text-sm font-medium px-2 min-w-[100px] text-center" style={{ color: colors.textPrimary }}>
                    {heatMapView === 'month' 
                      ? new Date(heatMapYear, heatMapMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                      : heatMapYear
                    }
                  </span>
                  <button
                    onClick={() => {
                      const now = new Date();
                      if (heatMapView === 'month') {
                        if (heatMapYear === now.getFullYear() && heatMapMonth >= now.getMonth()) return;
                        if (heatMapMonth === 11) {
                          setHeatMapYear(y => y + 1);
                          setHeatMapMonth(0);
                        } else {
                          setHeatMapMonth(m => m + 1);
                        }
                      } else {
                        setHeatMapYear(y => Math.min(y + 1, now.getFullYear()));
                      }
                    }}
                    disabled={heatMapView === 'year' ? heatMapYear >= new Date().getFullYear() : (heatMapYear === new Date().getFullYear() && heatMapMonth >= new Date().getMonth())}
                    className="p-1.5 rounded-lg transition-all hover:opacity-70 disabled:opacity-30"
                    style={{ backgroundColor: colors.bgSecondary }}
                  >
                    <ChevronRight size={16} style={{ color: colors.textSecondary }} />
                  </button>
                </div>
              )}
            </div>

            {hasAnalyticsAccess ? (
              <div
                className="pt-5 pb-2"
                style={{ borderTop: `1px solid ${colors.textPrimary}` }}
              >
                {/* Stats Row */}
                <div className="flex flex-wrap gap-4 sm:gap-8 mb-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.avgCompletionRate : heatMapData.stats.avgCompletionRate}%
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Avg Completion</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.warning }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.perfectDays : heatMapData.stats.perfectDays}
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Perfect Days</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.error }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.maxStreak : heatMapData.stats.maxStreak}
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Best Streak</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.textSecondary }}>
                      {heatMapView === 'month' ? heatMapData.monthStats?.totalCompletions : heatMapData.stats.totalCompletions}
                    </p>
                    <p className="text-xs font-medium" style={{ color: colors.textMuted }}>Total Completions</p>
                  </div>
                </div>

                {/* Month labels - only show for year view */}
                {heatMapView === 'year' && (
                  <div className="flex">
                    {/* Spacer to match day labels column */}
                    <div className="mr-1" style={{ width: '12px' }}></div>
                    {/* Month labels with same structure as weeks grid */}
                    <div className="flex gap-px flex-1 mb-2">
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, i) => (
                        <span
                          key={month}
                          className="text-xs"
                          style={{
                            color: colors.textMuted,
                            flex: heatMapData.monthWeekCounts?.[i] || 1,
                          }}
                        >
                          {month}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Week labels for month view */}
                {heatMapView === 'month' && (
                  <div className="mb-2 ml-8 flex justify-around">
                    {['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'].slice(0, heatMapData.monthWeeks.length).map((week, i) => (
                      <span
                        key={i}
                        className="text-xs text-center"
                        style={{ color: colors.textMuted }}
                      >
                        {week}
                      </span>
                    ))}
                  </div>
                )}

                {/* Heat Map Grid */}
                <div className="flex">
                  {/* Day labels - use same sizing as grid cells */}
                  <div className={`flex flex-col mr-1 ${heatMapView === 'month' ? 'gap-0.5' : 'gap-px'}`} style={{ width: heatMapView === 'month' ? '16px' : '12px' }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-center text-xs ${heatMapView === 'month' ? 'h-6' : 'aspect-square'}`}
                        style={{ color: colors.textMuted, fontSize: heatMapView === 'month' ? '11px' : '9px' }}
                      >
                        {heatMapView === 'month' ? day : (i % 2 === 1 ? day : '')}
                      </div>
                    ))}
                  </div>

                  {/* Weeks */}
                  <div className={`flex ${heatMapView === 'month' ? 'w-full' : 'gap-px flex-1'}`}>
                    {(heatMapView === 'month' ? heatMapData.monthWeeks : heatMapData.weeks).map((week, weekIndex) => (
                      <div key={weekIndex} className={`flex flex-col ${heatMapView === 'month' ? 'flex-1 gap-0.5' : 'gap-px flex-1'}`}>
                        {[0, 1, 2, 3, 4, 5, 6].map(dayOfWeek => {
                          const day = week.find(d => d.dayOfWeek === dayOfWeek);
                          return (
                            <div
                              key={dayOfWeek}
                              className={`rounded-sm transition-all  cursor-pointer ${heatMapView === 'month' ? 'h-6 mx-0.5' : 'aspect-square'}`}
                              style={{
                                backgroundColor: day ? getHeatMapColor(day.rate, day.isFuture) : (isDark ? `${colors.border}20` : `${colors.border}40`),
                              }}
                              title={day && !day.isFuture ? `${day.date}: ${day.completions}/${day.total} habits (${day.rate}%)` : day?.date || ''}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center justify-end gap-2 mt-4">
                  <span className="text-xs" style={{ color: colors.textMuted }}>Less</span>
                  <div className="flex gap-1">
                    {[0, 25, 50, 75, 100].map(rate => (
                      <div
                        key={rate}
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: getHeatMapColor(rate, false) }}
                      />
                    ))}
                  </div>
                  <span className="text-xs" style={{ color: colors.textMuted }}>More</span>
                </div>
              </div>
            ) : (
              <div
                className="pt-5 pb-2 text-center"
                style={{ borderTop: `1px solid ${colors.textPrimary}` }}
              >
                <Lock size={32} className="mx-auto mb-3" style={{ color: colors.textMuted }} />
                <p className="text-sm mb-1" style={{ color: colors.textPrimary }}>
                  Yearly Consistency Heat Map
                </p>
                <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
                  Visualize your habit completion patterns throughout the year
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-sm font-medium px-4 py-2 rounded-md transition-all "
                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                >
                  Upgrade to Pro
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Habit Modal */}
      {showHabitModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-w-lg p-5 sm:p-6 animate-scale-up sm:max-h-[85vh] overflow-y-auto"
            style={{ 
              ...getModalStyle(colors)
            }}
          >
            <div className="flex items-center justify-between mb-5 sm:mb-8">
              <h3 
                className="text-xl font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                {editingHabit ? 'Edit Habit' : 'New Habit'}
              </h3>
              <button 
                onClick={() => setShowHabitModal(false)} 
                className="p-2 -mr-2 rounded-full"
                style={{ color: colors.textMuted }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Habit Name
                </label>
                <input
                  type="text"
                  value={habitForm.name}
                  onChange={(e) => setHabitForm({ ...habitForm, name: e.target.value })}
                  placeholder="e.g., Morning meditation"
                  className="w-full px-4 py-4 sm:py-3 text-base sm:text-sm rounded-md outline-none transition-all duration-200 focus:ring-2"
                  style={{ 
                    backgroundColor: colors.bg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`
                  }}
                />
              </div>
              
              <div>
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  <Clock size={12} className="inline mr-1" />
                  Time of Day
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {timeOfDayOptions.map(option => {
                    const Icon = getTimeIcon(option.value);
                    const isSelected = habitForm.timeOfDay === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setHabitForm({ ...habitForm, timeOfDay: option.value })}
                        className="p-2 md:p-3 flex flex-col items-center gap-1 rounded-md transition-all duration-200 "
                        style={{
                          backgroundColor: isSelected ? colors.accent : colors.bg,
                          color: isSelected ? colors.bg : colors.textSecondary,
                          border: `1px solid ${isSelected ? colors.accent : colors.border}`
                        }}
                      >
                        <Icon size={16} />
                        <span className="text-[10px] md:text-xs">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Link to Goals Section */}
              {activeGoals.length > 0 && (
                <div>
                  <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                    <Link2 size={12} className="inline mr-1" />
                    Link to Goals
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {activeGoals.map(goal => {
                      const isLinked = selectedGoalIds.includes(goal.id);
                      return (
                        <button
                          key={goal.id}
                          onClick={() => {
                            if (isLinked) {
                              setSelectedGoalIds(selectedGoalIds.filter(id => id !== goal.id));
                            } else {
                              setSelectedGoalIds([...selectedGoalIds, goal.id]);
                            }
                          }}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all duration-200 "
                          style={{
                            backgroundColor: isLinked ? `${colors.accent}20` : colors.bg,
                            color: isLinked ? colors.accent : colors.textSecondary,
                            border: `1px solid ${isLinked ? colors.accent : colors.border}`
                          }}
                        >
                          <Target size={14} />
                          <span className="text-xs font-medium">{goal.title}</span>
                          {isLinked && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                  {selectedGoalIds.length > 0 && (
                    <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                      {selectedGoalIds.length} goal{selectedGoalIds.length !== 1 ? 's' : ''} linked
                    </p>
                  )}
                </div>
              )}
              
              <div className={`flex ${editingHabit ? 'gap-3' : ''}`}>
                {editingHabit && (
                  <button
                    onClick={() => {
                      handleDeleteHabit(editingHabit.id);
                      setShowHabitModal(false);
                      setEditingHabit(null);
                    }}
                    className="py-3.5 px-4 text-xs rounded-md transition-all duration-200 font-medium"
                    style={{ 
                      backgroundColor: 'transparent',
                      color: colors.error,
                      border: `1px solid ${colors.error}`
                    }}
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={handleSaveHabit}
                  className="flex-1 py-3.5 text-xs rounded-md transition-all duration-200 font-medium"
                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                >
                  {editingHabit ? 'Save Changes' : 'Add Habit'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Unlink Warning Modal */}
      {unlinkWarningHabit && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fade-in">
          <div 
            className="w-full max-w-sm p-5 sm:p-6 rounded-lg animate-scale-up"
            style={{ backgroundColor: colors.bg, border: `1px solid ${colors.textPrimary}` }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="p-2 rounded-full"
                style={{ backgroundColor: `${colors.warning}20` }}
              >
                <AlertTriangle size={20} style={{ color: colors.warning }} />
              </div>
              <h3 
                className="text-lg font-semibold"
                style={{ color: colors.textPrimary }}
              >
                Delete Linked Habit?
              </h3>
            </div>
            
            <p className="text-sm mb-3" style={{ color: colors.textSecondary }}>
              <strong>{unlinkWarningHabit.habit.name}</strong> is linked to the following active goals:
            </p>
            
            <ul className="mb-4 space-y-1.5">
              {unlinkWarningHabit.activeLinkedGoals.map(goal => (
                <li 
                  key={goal.id}
                  className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.bg }}
                >
                  <Target size={14} style={{ color: colors.accent }} />
                  <span style={{ color: colors.textPrimary }}>{goal.title}</span>
                </li>
              ))}
            </ul>
            
            <p className="text-xs mb-4" style={{ color: colors.textMuted }}>
              Deleting this habit will unlink it from these goals. The goals will remain but lose this habit's consistency data.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setUnlinkWarningHabit(null)}
                className="flex-1 py-2.5 text-xs rounded-md transition-all font-medium"
                style={{ 
                  backgroundColor: colors.bg,
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteWithUnlink}
                className="flex-1 py-2.5 text-xs rounded-md transition-all font-medium"
                style={{ 
                  backgroundColor: colors.error,
                  color: colors.onAccent
                }}
              >
                Delete Anyway
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Templates Modal */}
      {showTemplateModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
          <div 
            className="sheet-safe w-full h-full sm:h-auto sm:max-w-lg p-5 sm:p-6 sm:max-h-[85vh] overflow-y-auto animate-scale-up"
            style={{ 
              ...getModalStyle(colors)
            }}
          >
            <div className="flex items-center justify-between mb-5 sm:mb-8">
              <h3 
                className="text-xl font-semibold tracking-tight"
                style={{ color: colors.textPrimary }}
              >
                Habit Templates
              </h3>
              <button 
                onClick={() => setShowTemplateModal(false)} 
                className="p-2 -mr-2 rounded-full"
                style={{ color: colors.textMuted }}
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Save Current as Template - Pro only */}
            {habits.length > 0 && (
              <div className="mb-8">
                <label className="block text-xs mb-2 font-medium" style={{ color: colors.textMuted }}>
                  Save Current Habits as Template
                  {!isPro && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>PRO</span>}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={isPro ? "Template name..." : "Upgrade to Pro to save templates"}
                    disabled={!isPro}
                    className="flex-1 px-4 py-4 sm:py-3 text-base sm:text-sm rounded-md outline-none transition-all duration-200 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: colors.bg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`
                    }}
                  />
                  <button
                    onClick={handleSaveTemplate}
                    disabled={!isPro}
                    className="px-4 py-3 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                  >
                    <Save size={16} />
                  </button>
                </div>
              </div>
            )}
            
            {/* Starter Templates */}
            <div className="mb-6">
              <p className="text-xs mb-3 font-medium" style={{ color: colors.textMuted }}>
                Starter Templates
                {!isPro && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>PRO</span>}
              </p>
              <div className="space-y-2">
                {HABIT_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => isPro && handleLoadStarterTemplate(template)}
                    disabled={!isPro}
                    className={`w-full p-4 text-left rounded-md transition-all duration-200 ${isPro ? ' cursor-pointer' : 'cursor-not-allowed'}`}
                    style={{ 
                      backgroundColor: colors.bg,
                      border: `1px solid ${colors.border}`,
                      opacity: isPro ? 1 : 0.5,
                      filter: isPro ? 'none' : 'grayscale(50%)',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium" style={{ color: colors.textPrimary }}>
                          {template.name}
                        </p>
                        <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                          {template.description}
                        </p>
                      </div>
                      {isPro ? (
                        <Download size={16} style={{ color: colors.textSecondary }} />
                      ) : (
                        <Lock size={16} style={{ color: colors.textMuted }} />
                      )}
                    </div>
                  </button>
                ))}
                {!isPro && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="w-full py-2 text-sm font-medium hover:underline transition-all"
                    style={{ color: colors.accent }}
                  >
                    Upgrade to Pro to use templates →
                  </button>
                )}
              </div>
            </div>
            
            {/* Saved Templates - Only show for Pro users with templates */}
            {isPro && templates.length > 0 && (
              <div>
                <p className="text-xs mb-3 font-medium" style={{ color: colors.textMuted }}>
                  Your Templates
                </p>
                <div className="space-y-2">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className="p-4 flex items-center justify-between rounded-md transition-all duration-200"
                      style={{ 
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <div>
                        <p style={{ color: colors.textPrimary, fontWeight: 600, letterSpacing: '-0.035em' }}>{template.name}</p>
                        <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                          {template.habits.length} habits • Saved {new Date(template.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleLoadTemplate(template)}
                          className="p-2 rounded-lg transition-all duration-200 "
                          style={{ color: colors.accent }}
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => deleteTemplate(template.id)}
                          className="p-2 rounded-lg transition-all duration-200 "
                          style={{ color: colors.error }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {templates.length === 0 && habits.length === 0 && (
              <p className="text-center py-4" style={{ color: colors.textMuted }}>
                Choose a starter template above to get started!
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
      
      {/* Upgrade Limit Modal */}
      <UpgradeLimitModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        itemType="habits"
        currentCount={habits.length}
        limit={habitLimit}
      />
    </div>
  );
};

export default HabitsPage;
